import { DEMO_REPLAY } from "./demoData.js";
import { readJsonResponse } from "./apiErrors.js";

export class MarketReplayClient {
  constructor({ baseUrl, getToken }) {
    this.baseUrl = String(baseUrl || "/api").replace(/\/+$/, "");
    this.getToken = getToken;
  }

  async session() {
    return this.get("/session");
  }

  async runs() {
    return this.get("/runs");
  }

  async methodology() {
    return this.get("/methodology");
  }

  async run(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}`);
  }

  async windows(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}/windows`);
  }

  async series(runId, windowId = "") {
    const query = windowId
      ? `?window=${encodeURIComponent(windowId)}`
      : "";
    return this.get(`/runs/${encodeURIComponent(runId)}/series${query}`);
  }

  async costs(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}/costs`);
  }

  async taxes(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}/taxes`);
  }

  async holdings(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}/holdings`);
  }

  async decisions(runId) {
    return this.get(`/runs/${encodeURIComponent(runId)}/decisions`);
  }

  async fullHoldings(runId, windowId, strategy) {
    return this.get(
      `/runs/${encodeURIComponent(runId)}/holdings/${encodeURIComponent(
        windowId,
      )}/${encodeURIComponent(strategy)}`,
    );
  }

  async fullDecisions(runId, windowId, strategy) {
    return this.get(
      `/runs/${encodeURIComponent(runId)}/decisions/${encodeURIComponent(
        windowId,
      )}/${encodeURIComponent(strategy)}`,
    );
  }

  async portfolios() {
    return this.get("/portfolios");
  }

  async createPortfolio(portfolio) {
    return this.request("/portfolios", {
      body: portfolio,
      method: "POST",
    });
  }

  async portfolio(portfolioId) {
    return this.get(`/portfolios/${encodeURIComponent(portfolioId)}`);
  }

  async updatePortfolio(portfolioId, portfolio) {
    return this.request(`/portfolios/${encodeURIComponent(portfolioId)}`, {
      body: portfolio,
      method: "PUT",
    });
  }

  async deletePortfolio(portfolioId) {
    return this.request(`/portfolios/${encodeURIComponent(portfolioId)}`, {
      method: "DELETE",
    });
  }

  async portfolioRecommendation(portfolioId, recommendationId = "latest") {
    return this.get(
      `/portfolios/${encodeURIComponent(
        portfolioId,
      )}/recommendations/${encodeURIComponent(recommendationId)}`,
    );
  }

  async portfolioRecommendations(portfolioId) {
    return this.get(
      `/portfolios/${encodeURIComponent(portfolioId)}/recommendations`,
    );
  }

  async refreshPortfolioRecommendation(portfolioId) {
    return this.request(
      `/portfolios/${encodeURIComponent(portfolioId)}/recommendations/latest`,
      {
        method: "POST",
      },
    );
  }

  async previewPortfolioRecommendation(portfolioId) {
    return this.request(
      `/portfolios/${encodeURIComponent(
        portfolioId,
      )}/recommendations/preview-today`,
      {
        method: "POST",
      },
    );
  }

  async executeTrade(portfolioId, trade) {
    return this.request(`/portfolios/${encodeURIComponent(portfolioId)}/trades`, {
      body: trade,
      method: "POST",
    });
  }

  async completePortfolioRebalance(portfolioId) {
    return this.request(
      `/portfolios/${encodeURIComponent(portfolioId)}/rebalance/complete`,
      { method: "POST" },
    );
  }

  async portfolioActivity(portfolioId) {
    return this.get(`/portfolios/${encodeURIComponent(portfolioId)}/activity`);
  }

  async portfolioTaxes(portfolioId) {
    return this.get(
      `/portfolios/${encodeURIComponent(portfolioId)}/tax-payments`,
    );
  }

  async recordTaxPayment(portfolioId, payment) {
    return this.request(
      `/portfolios/${encodeURIComponent(portfolioId)}/tax-payments`,
      {
        body: payment,
        method: "POST",
      },
    );
  }

  async portfolioPerformance(
    portfolioId,
    period = "monthly",
    startDate = "",
    options = {},
  ) {
    const query = new URLSearchParams({ period });
    if (startDate) {
      query.set("start_date", startDate);
    }
    return this.get(
      `/portfolios/${encodeURIComponent(portfolioId)}/performance?${query}`,
      options,
    );
  }

  async portfolioPerformanceSummary(portfolioId) {
    return this.get(
      `/portfolios/${encodeURIComponent(portfolioId)}/performance-summary`,
    );
  }

  async recordCashFlow(portfolioId, cashFlow) {
    return this.request(
      `/portfolios/${encodeURIComponent(portfolioId)}/cash-flows`,
      {
        body: cashFlow,
        method: "POST",
      },
    );
  }

  async recordDividend(portfolioId, dividend) {
    return this.request(
      `/portfolios/${encodeURIComponent(portfolioId)}/dividends`,
      {
        body: dividend,
        method: "POST",
      },
    );
  }

  async tickerState(ticker) {
    return this.get(`/ticker-state/${encodeURIComponent(ticker)}`);
  }

  async tickerStates(tickers) {
    const query = new URLSearchParams({
      tickers: (tickers || []).join(","),
    });
    return this.get(`/ticker-states?${query}`);
  }

  async get(path, options = {}) {
    return this.request(path, options);
  }

  async request(path, { body, method = "GET", signal } = {}) {
    const token = await this.getToken(false);
    const response = await this.fetchWithToken(path, {
      body,
      method,
      signal,
      token,
    });
    if (response.status === 401) {
      const refreshedToken = await this.getToken(true);
      if (refreshedToken && refreshedToken !== token) {
        return this.readResponse(
          await this.fetchWithToken(path, {
            body,
            method,
            signal,
            token: refreshedToken,
          }),
        );
      }
    }
    return this.readResponse(response);
  }

  async fetchWithToken(path, { body, method, signal, token }) {
    if (!token) {
      throw new Error("Google login token is not available.");
    }
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    return fetch(`${this.baseUrl}${path}`, {
      body: body === undefined ? undefined : JSON.stringify(body),
      headers,
      method,
      signal,
    });
  }

  async readResponse(response) {
    return readJsonResponse(response);
  }
}

export class DemoMarketReplayClient {
  constructor({ artifactBase } = {}) {
    this.artifactBase = String(artifactBase || "").replace(/\/+$/, "");
    this.demoActivity = [...DEMO_ACTIVITY];
    this.demoExecutedActions = new Set();
    this.demoRebalanceCompleted = false;
    this.demoPortfolios = DEMO_PORTFOLIOS;
    this.demoTaxPayments = [];
  }

  async session() {
    return { authorized: true, demo: true, email: "local-demo@jestle.com" };
  }

  async runs() {
    const localIndex = await this.tryArtifact("manifests/index.json");
    if (localIndex) {
      return localIndex;
    }
    const localEntry = await this.tryArtifact("index_entry.json");
    if (localEntry) {
      return { schema_version: 1, runs: [localEntry] };
    }
    return DEMO_REPLAY.index;
  }

  async methodology() {
    return {
      lede: (
        "In production this document is served by the authenticated market "
        + "replay API after Google login."
      ),
      schema_version: 1,
      sections: [
        {
          paragraphs: [
            "Server-served strategy documentation is available after Google login.",
          ],
          tab: "strategy",
          title: "Current Default Strategy",
        },
        {
          paragraphs: [
            "Server-served simulator documentation is available after Google login.",
          ],
          tab: "simulator",
          title: "Execution Prices",
        },
        {
          paragraphs: [
            "Server-served evaluation documentation is available after Google login.",
          ],
          tab: "evaluation",
          title: "Long-History Results",
        },
        {
          paragraphs: [
            "Server-served ablation documentation is available after Google login.",
          ],
          tab: "ablations",
          title: "Ablation Study Summary",
        },
      ],
      title: "Market Replay Methodology",
    };
  }

  async run() {
    return (await this.tryArtifact("manifest.json")) || DEMO_REPLAY.manifest;
  }

  async windows() {
    return (await this.tryArtifact("windows.json")) || DEMO_REPLAY.windows;
  }

  async series(_runId = "", windowId = "") {
    if (windowId) {
      const localFull = await this.tryArtifact(
        `series/${encodeURIComponent(windowId)}.json`,
      );
      if (localFull) {
        return localFull;
      }
      const inline =
        (await this.tryArtifact("series.json")) || DEMO_REPLAY.series;
      return {
        schema_version: inline.schema_version || 1,
        series_kind: inline.series_kind || "monthly_rebalance",
        windows: {
          [windowId]: inline.windows?.[windowId] || {},
        },
      };
    }
    return (await this.tryArtifact("series.json")) || DEMO_REPLAY.series;
  }

  async costs() {
    return (await this.tryArtifact("costs.json")) || DEMO_REPLAY.costs;
  }

  async taxes() {
    return (await this.tryArtifact("taxes.json")) || DEMO_REPLAY.taxes;
  }

  async holdings() {
    return (await this.tryArtifact("holdings.json")) || DEMO_REPLAY.holdings;
  }

  async decisions() {
    return (await this.tryArtifact("decisions.json")) || DEMO_REPLAY.decisions;
  }

  async fullHoldings(_runId, windowId, strategy) {
    const localFull = await this.tryArtifact(
      `holdings/${encodeURIComponent(windowId)}/${encodeURIComponent(
        strategy,
      )}.json`,
    );
    if (localFull) {
      return localFull;
    }
    return {
      dates: this.holdingsForDemoWindow(windowId, strategy),
      holdings_kind: "demo_full_strategy",
      schema_version: 1,
      strategy,
      window_id: windowId,
    };
  }

  async fullDecisions(_runId, windowId, strategy) {
    const localFull = await this.tryArtifact(
      `decisions/${encodeURIComponent(windowId)}/${encodeURIComponent(
        strategy,
      )}.json`,
    );
    if (localFull) {
      return localFull;
    }
    return {
      dates: this.decisionsForDemoWindow(windowId, strategy),
      decisions_kind: "demo_full_strategy",
      schema_version: 1,
      strategy,
      window_id: windowId,
    };
  }

  async portfolios() {
    return {
      portfolios: this.demoPortfolios.map((portfolio) => ({
        holding_count: portfolio.holdings.length,
        name: portfolio.name,
        owner_email_hash: "demo",
        portfolio_id: portfolio.portfolio_id,
        role: "owner",
        updated_at: portfolio.updated_at,
      })),
      schema_version: 1,
    };
  }

  async createPortfolio(portfolio) {
    const now = new Date().toISOString();
    const created = normalizeDemoPortfolio({
      ...portfolio,
      created_at: now,
      portfolio_id: `demo-${Date.now()}`,
      updated_at: now,
    });
    this.demoPortfolios.unshift(created);
    return created;
  }

  async portfolio(portfolioId) {
    const portfolio = this.demoPortfolios.find(
      (row) => row.portfolio_id === portfolioId,
    );
    if (!portfolio) {
      throw new Error(`/portfolios/${portfolioId} returned 404`);
    }
    return { ...portfolio, permissions: { can_read: true, can_write: true } };
  }

  async updatePortfolio(portfolioId, portfolio) {
    const index = this.demoPortfolios.findIndex(
      (row) => row.portfolio_id === portfolioId,
    );
    if (index < 0) {
      throw new Error(`/portfolios/${portfolioId} returned 404`);
    }
    const updated = normalizeDemoPortfolio({
      ...portfolio,
      portfolio_id: portfolioId,
      updated_at: new Date().toISOString(),
    });
    this.demoPortfolios[index] = updated;
    return updated;
  }

  async deletePortfolio(portfolioId) {
    const index = this.demoPortfolios.findIndex(
      (row) => row.portfolio_id === portfolioId,
    );
    if (index < 0) {
      throw new Error(`/portfolios/${portfolioId} returned 404`);
    }
    this.demoPortfolios.splice(index, 1);
    return { deleted: true, portfolio_id: portfolioId };
  }

  async portfolioRecommendation(portfolioId) {
    const portfolio = await this.portfolio(portfolioId);
    const actions = demoRecommendationActions(
      this.demoExecutedActions,
      portfolio,
    );
    const estimatedTax = actions.reduce(
      (total, action) => total + Number(action.estimated_tax || 0),
      0,
    );
    const estimatedTransactionCost = actions.reduce(
      (total, action) =>
        total + Number(action.estimated_transaction_cost || 0),
      0,
    );
    const pendingCount = actions.filter((action) => action.status !== "executed")
      .length;
    const executedCount = actions.length - pendingCount;
    return {
      actions,
      as_of_date: "2026-06-01",
      generated_at: "2026-06-01T12:00:00Z",
      holdings: [
        {
          cost_basis: 1200,
          lots: [
            {
              cost_basis: 1200,
              cost_basis_per_share: 120,
              long_term: true,
              market_value: 3400,
              price: 340,
              purchase_date: "2020-01-01",
              shares: 10,
              unrealized_gain: 2200,
              unrealized_gain_percent: 1.8333333333,
            },
          ],
          market_value: 3400,
          price: 340,
          price_as_of_date: "2026-06-01",
          price_source: "market",
          shares: 10,
          strategy_status: "market",
          ticker: "MSFT",
          unrealized_gain: 2200,
          unrealized_gain_percent: 1.8333333333,
        },
        {
          cost_basis: 720,
          lots: [
            {
              cost_basis: 450,
              cost_basis_per_share: 90,
              long_term: true,
              market_value: 900,
              price: 180,
              purchase_date: "2020-01-01",
              shares: 5,
              unrealized_gain: 450,
              unrealized_gain_percent: 1,
            },
            {
              cost_basis: 270,
              cost_basis_per_share: 90,
              long_term: true,
              market_value: 540,
              price: 180,
              purchase_date: "2021-06-01",
              shares: 3,
              unrealized_gain: 270,
              unrealized_gain_percent: 1,
            },
          ],
          market_value: 1440,
          price: 180,
          price_as_of_date: "2026-06-01",
          price_source: "market",
          shares: 8,
          strategy_status: "market",
          ticker: "GOOGL",
          unrealized_gain: 720,
          unrealized_gain_percent: 1,
        },
      ],
      portfolio_id: portfolio.portfolio_id,
      portfolio_name: portfolio.name,
      rebalance_schedule: {
        activation_date: portfolio.activation_date || "2026-04-01",
        as_of_date: "2026-06-04",
        days_until_next: this.demoRebalanceCompleted ? 29 : -1,
        frequency: "monthly",
        last_completed_rebalance_date: this.demoRebalanceCompleted
          ? "2026-06-03"
          : "",
        next_rebalance_date: this.demoRebalanceCompleted
          ? "2026-07-03"
          : "2026-06-03",
        scheduled_rebalance_date: this.demoRebalanceCompleted
          ? "2026-07-03"
          : "2026-06-03",
        status: this.demoRebalanceCompleted ? "upcoming" : "overdue",
      },
      recommendation_id: "demo-latest",
      schema_version: 1,
      summary: {
        annual_tax_budget: 193.6,
        cash_after_estimate: 1575,
        cash_before: Number(portfolio.cash || 0),
        estimated_tax: estimatedTax,
        estimated_transaction_cost: estimatedTransactionCost,
        holdings_cost_basis: 1920,
        holdings_market_value: 4840,
        portfolio_value: 4840,
        remaining_tax_budget: 1880,
        trade_count: actions.length,
        unrealized_gain: 2920,
        unrealized_gain_percent: 1.5208333333,
      },
      target: {
        age_days: 1,
        as_of_date: "2026-06-01",
        max_age_days: 7,
        price_count: 20,
        schema_version: 1,
        status: "fresh",
        strategy: "volume",
        target_count: 20,
        volume_is_lagged: true,
        volume_lookback_days: 20,
      },
      workflow: {
        action_count: actions.length,
        executed_action_count: executedCount,
        partial_action_count: 0,
        pending_action_count: pendingCount,
        status: pendingCount ? "pending" : "complete",
      },
      warnings: [],
    };
  }

  async portfolioRecommendations(portfolioId) {
    const recommendation = await this.portfolioRecommendation(portfolioId);
    return {
      portfolio_id: portfolioId,
      recommendations: [recommendationSummary("demo-latest", recommendation)],
      schema_version: 1,
    };
  }

  async refreshPortfolioRecommendation(portfolioId) {
    return this.portfolioRecommendation(portfolioId);
  }

  async previewPortfolioRecommendation(portfolioId) {
    const recommendation = await this.portfolioRecommendation(portfolioId);
    return {
      ...recommendation,
      preview: true,
      recommendation_id: "demo-preview-today",
      rebalance_schedule: {
        ...(recommendation.rebalance_schedule || {}),
        preview_rebalance_date: "2026-06-01",
        status: "preview",
      },
    };
  }

  async executeTrade(portfolioId, trade) {
    const portfolio = await this.portfolio(portfolioId);
    const result = applyDemoTrade(portfolio, trade);
    this.replaceDemoPortfolio(result.portfolio);
    this.demoActivity.unshift(demoActivityFromTrade(result.trade));
    this.markDemoRecommendationExecuted(trade);
    return result;
  }

  async completePortfolioRebalance(portfolioId) {
    const portfolio = await this.portfolio(portfolioId);
    const recommendation = await this.portfolioRecommendation(portfolioId);
    const completedAt = new Date().toISOString();
    const completedActions = recommendation.actions.map((action) =>
      ["pending", "partial"].includes(action.status)
        ? {
            ...action,
            skip_reason: "owner_completed_rebalance",
            skipped_at: completedAt,
            status: "skipped",
          }
        : action,
    );
    const skippedActionCount = completedActions.filter(
      (action) => action.status === "skipped",
    ).length;
    const updated = normalizeDemoPortfolio({
      ...portfolio,
      rebalance: {
        ...(portfolio.rebalance || {}),
        last_completed_rebalance_date: "2026-06-03",
      },
      updated_at: new Date().toISOString(),
    });
    this.demoRebalanceCompleted = true;
    this.replaceDemoPortfolio(updated);
    const completedSchedule = {
      ...(recommendation.rebalance_schedule || {}),
      last_completed_rebalance_date: "2026-06-03",
      status: "completed",
    };
    return {
      portfolio: updated,
      recommendation: {
        ...recommendation,
        actions: completedActions,
        current_rebalance_schedule: completedSchedule,
        workflow: {
          ...(recommendation.workflow || {}),
          completed_at: completedAt,
          completion_method: "owner_confirmed",
          partial_action_count: 0,
          pending_action_count: 0,
          skipped_action_count: skippedActionCount,
          status: "complete",
        },
      },
      schema_version: 1,
    };
  }

  async portfolioActivity(portfolioId) {
    const recommendation = await this.portfolioRecommendation(portfolioId);
    return {
      activity: [
        ...this.demoActivity,
        {
          activity_id: "demo-latest",
          activity_type: "recommendation",
          details: recommendationSummary("demo-latest", recommendation),
          recorded_at: recommendation.generated_at,
          title: (
            `Recommendation generated: ${recommendation.actions.length} `
            + "proposed trades"
          ),
        },
      ],
      portfolio_id: portfolioId,
      schema_version: 1,
    };
  }

  async portfolioTaxes(portfolioId) {
    await this.portfolio(portfolioId);
    const federalPaid = this.demoTaxPayments
      .filter((payment) => payment.jurisdiction === "federal")
      .reduce((total, payment) => total + payment.amount, 0);
    const statePaid = this.demoTaxPayments
      .filter((payment) => payment.jurisdiction === "state")
      .reduce((total, payment) => total + payment.amount, 0);
    return {
      as_of_date: "2026-07-27",
      can_write: true,
      portfolio_id: portfolioId,
      portfolio_only: true,
      schema_version: 1,
      tax_rates: {
        federal_long_term_rate: 0.2,
        federal_short_term_rate: 0.37,
        niit_rate: 0.038,
        state_rate: 0.044,
      },
      years: [
        {
          federal: {
            estimated_liability: 8750,
            paid: federalPaid,
            remaining: Math.max(0, 8750 - federalPaid),
          },
          income: {
            long_term_gain: 30000,
            ordinary_income: 400,
            qualified_dividend_income: 600,
            short_term_gain: 2500,
          },
          payment_dates: [
            {
              due_date: "2026-04-15",
              label: "Estimated payment 1",
              status: "past",
            },
            {
              due_date: "2026-06-15",
              label: "Estimated payment 2",
              status: "past",
            },
            {
              due_date: "2026-09-15",
              label: "Estimated payment 3",
              status: "upcoming",
            },
            {
              due_date: "2027-01-15",
              label: "Estimated payment 4",
              status: "upcoming",
            },
            {
              due_date: "2027-04-15",
              label: "Annual balance",
              status: "upcoming",
            },
          ],
          payments: [...this.demoTaxPayments],
          state: {
            estimated_liability: 1474,
            paid: statePaid,
            remaining: Math.max(0, 1474 - statePaid),
          },
          state_code: "CO",
          tax_year: 2026,
        },
      ],
    };
  }

  async recordTaxPayment(portfolioId, payment) {
    const portfolio = await this.portfolio(portfolioId);
    const amount = Number(payment.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Invalid tax payment");
    }
    if (amount > Number(portfolio.cash || 0)) {
      throw new Error("Tax payment exceeds available cash");
    }
    const jurisdiction = String(payment.jurisdiction || "").toLowerCase();
    if (!["federal", "state"].includes(jurisdiction)) {
      throw new Error("Invalid tax jurisdiction");
    }
    const taxPayment = {
      amount,
      effective_date: payment.effective_date,
      jurisdiction,
      note: payment.note || "",
      tax_year: Number(payment.tax_year),
    };
    const updated = normalizeDemoPortfolio({
      ...portfolio,
      cash: Number(portfolio.cash || 0) - amount,
      updated_at: new Date().toISOString(),
    });
    this.replaceDemoPortfolio(updated);
    this.demoTaxPayments.unshift(taxPayment);
    this.demoActivity.unshift(demoActivityFromTaxPayment(taxPayment));
    return {
      portfolio: updated,
      schema_version: 1,
      tax_payment: taxPayment,
    };
  }

  async portfolioPerformance(
    portfolioId,
    period = "monthly",
    startDate = "",
    _options = {},
  ) {
    const effectiveStartDate = startDate || "2026-04-01";
    const points = rollupDemoPerformancePoints(
      [
        {
          actual_costs: 0,
          actual_estimated_tax: 0,
          actual_trade_count: 0,
          benchmarks: {
            inception_hold: { return_since_start: 0, value: 4300 },
            nasdaq100: { return_since_start: 0.012, value: 4351.6 },
            nasdaq_composite: { return_since_start: 0.006, value: 4325.8 },
            spy: { return_since_start: 0.008, value: 4334.4 },
          },
          cash: 0,
          contributions: 0,
          date: "2026-04-01",
          holdings_market_value: 4300,
          net_external_cash_flow: 0,
          period: "2026-04",
          portfolio_value: 4300,
          return_since_start: 0,
          unrealized_gain: 2380,
          withdrawals: 0,
          volume_plan_estimated_cost: 8,
          volume_plan_estimated_tax: 35,
          volume_plan_return_since_start: 0,
          volume_plan_trade_count: 3,
          volume_plan_value: 4257,
          volume_replay_costs: 4,
          volume_replay_estimated_tax: 0,
          volume_replay_return_since_start: -0.001,
          volume_replay_trade_count: 1,
          volume_replay_value: 4295.7,
        },
        {
          actual_costs: 1,
          actual_estimated_tax: 0,
          actual_trade_count: 1,
          benchmarks: {
            inception_hold: { return_since_start: 0.055, value: 4736.5 },
            nasdaq100: { return_since_start: 0.086, value: 4669.8 },
            nasdaq_composite: { return_since_start: 0.052, value: 4523.6 },
            spy: { return_since_start: 0.061, value: 4762.3 },
          },
          cash: 0,
          contributions: 500,
          date: "2026-05-01",
          holdings_market_value: 4625,
          net_external_cash_flow: 500,
          period: "2026-05",
          portfolio_value: 4625,
          return_since_start: -0.0406976744,
          unrealized_gain: 2705,
          withdrawals: 0,
          volume_plan_estimated_cost: 7,
          volume_plan_estimated_tax: 42,
          volume_plan_return_since_start: 0.0742306789,
          volume_plan_trade_count: 2,
          volume_plan_value: 4576,
          volume_replay_costs: 9,
          volume_replay_estimated_tax: 12,
          volume_replay_return_since_start: 0.0818604651,
          volume_replay_trade_count: 3,
          volume_replay_value: 4652,
        },
        {
          actual_costs: 1,
          actual_estimated_tax: 11,
          actual_trade_count: 2,
          benchmarks: {
            inception_hold: { return_since_start: 0.079, value: 4840 },
            nasdaq100: { return_since_start: 0.142, value: 4910.6 },
            nasdaq_composite: { return_since_start: 0.091, value: 4691.3 },
            spy: { return_since_start: 0.097, value: 4717.1 },
          },
          cash: 0,
          contributions: 500,
          date: "2026-06-01",
          holdings_market_value: 4840,
          net_external_cash_flow: 500,
          period: "2026-06",
          portfolio_value: 4840,
          return_since_start: 0.0093023256,
          unrealized_gain: 2920,
          withdrawals: 0,
          volume_plan_estimated_cost: 5,
          volume_plan_estimated_tax: 28,
          volume_plan_return_since_start: 0.1291999999,
          volume_plan_trade_count: 1,
          volume_plan_value: 4807,
          volume_replay_costs: 13,
          volume_replay_estimated_tax: 18,
          volume_replay_return_since_start: 0.1534883721,
          volume_replay_trade_count: 4,
          volume_replay_value: 4960,
        },
      ],
      period,
    );
    return {
      initial_value: 4300,
      period,
      points,
      portfolio_id: portfolioId,
      requested_start_date: effectiveStartDate,
      schema_version: 1,
      start_date: effectiveStartDate,
    };
  }

  async portfolioPerformanceSummary(portfolioId) {
    const performance = await this.portfolioPerformance(
      portfolioId,
      "yearly",
      "",
    );
    const latest = performance.points[performance.points.length - 1] || {};
    const returnSinceStart = Number(
      latest.return_after_tax_since_start ?? latest.return_since_start,
    );
    return {
      after_tax_value: Number(
        latest.portfolio_after_tax_value ?? latest.portfolio_value,
      ),
      as_of_date: latest.date || "",
      cache_status: "hit",
      gain_loss: performance.initial_value * returnSinceStart,
      gain_loss_percent: returnSinceStart,
      portfolio_id: portfolioId,
      schema_version: 1,
      start_date: performance.start_date,
    };
  }

  async recordCashFlow(portfolioId, cashFlow) {
    const portfolio = this.demoPortfolios.find(
      (row) => row.portfolio_id === portfolioId,
    );
    if (!portfolio) {
      throw new Error("Portfolio not found");
    }
    const action = String(cashFlow.action || "").toUpperCase();
    const amount = Number(cashFlow.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Invalid cash flow");
    }
    const delta = action === "WITHDRAWAL" ? -amount : amount;
    if (Number(portfolio.cash || 0) + delta < 0) {
      throw new Error("Cash withdrawal exceeds available cash");
    }
    const updated = normalizeDemoPortfolio({
      ...portfolio,
      cash: Number(portfolio.cash || 0) + delta,
      updated_at: new Date().toISOString(),
    });
    this.demoPortfolios = this.demoPortfolios.map((row) =>
      row.portfolio_id === portfolioId ? updated : row,
    );
    this.demoActivity.unshift(
      demoActivityFromCashFlow({
        action,
        amount,
        cash_delta: delta,
        effective_date: cashFlow.effective_date,
      }),
    );
    return {
      cash_flow: {
        action,
        amount,
        cash_delta: delta,
        effective_date: cashFlow.effective_date,
      },
      portfolio: updated,
      schema_version: 1,
    };
  }

  async recordDividend(portfolioId, dividend) {
    const portfolio = this.demoPortfolios.find(
      (row) => row.portfolio_id === portfolioId,
    );
    if (!portfolio) {
      throw new Error("Portfolio not found");
    }
    const amount = Number(dividend.amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Invalid dividend");
    }
    const updated = normalizeDemoPortfolio({
      ...portfolio,
      cash: Number(portfolio.cash || 0) + amount,
      updated_at: new Date().toISOString(),
    });
    this.demoPortfolios = this.demoPortfolios.map((row) =>
      row.portfolio_id === portfolioId ? updated : row,
    );
    this.demoActivity.unshift(
      demoActivityFromDividend({
        amount,
        effective_date: dividend.effective_date,
        estimated_tax: amount * 0.452,
        ticker: dividend.ticker,
      }),
    );
    return {
      dividend: {
        amount,
        cash_delta: amount,
        effective_date: dividend.effective_date,
        estimated_tax: amount * 0.452,
        ticker: dividend.ticker,
      },
      portfolio: updated,
      schema_version: 1,
    };
  }

  async tickerState(ticker) {
    const tickerKey = String(ticker || "").trim().toUpperCase();
    const recommendation = await this.portfolioRecommendation("demo-taxable");
    const holding = recommendation.holdings.find(
      (row) => row.ticker === tickerKey,
    );
    const fallbackPrices = {
      AAPL: 150,
      AMZN: 180,
      NVDA: 180,
      TSLA: 220,
    };
    const price = holding?.price || fallbackPrices[tickerKey];
    if (!tickerKey || !price) {
      throw new Error(`/ticker-state/${tickerKey} returned 404`);
    }
    return {
      as_of_date: recommendation.as_of_date,
      metadata: {
        strategy: "demo",
      },
      schema_version: 1,
      source_path: "demo",
      state: {
        currency: "USD",
        dollar_volume_20d: null,
        in_target_universe: tickerKey === "NVDA",
        price,
        target_weight: tickerKey === "NVDA" ? 0.05 : null,
        trading_days: tickerKey === "NVDA" ? 260 : 300,
      },
      ticker: tickerKey,
    };
  }

  async tickerStates(tickers) {
    const states = {};
    for (const ticker of tickers || []) {
      const tickerKey = String(ticker || "").trim().toUpperCase();
      try {
        states[tickerKey] = {
          ...(await this.tickerState(tickerKey)),
          availability: "available",
        };
      } catch {
        states[tickerKey] = {
          availability: "unsupported",
          ticker: tickerKey,
        };
      }
    }
    return {
      availability: "available",
      schema_version: 1,
      states,
    };
  }

  holdingsForDemoWindow(windowId, strategy) {
    return DEMO_REPLAY.holdings.windows?.[windowId]?.[strategy] || {};
  }

  decisionsForDemoWindow(windowId, strategy) {
    return DEMO_REPLAY.decisions.windows?.[windowId]?.[strategy] || {};
  }

  async tryArtifact(path) {
    if (!this.artifactBase) {
      return null;
    }
    try {
      const response = await fetch(`${this.artifactBase}/${path}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        return null;
      }
      const text = await response.text();
      const trimmed = text.trimStart();
      if (!trimmed || trimmed.startsWith("<")) {
        return null;
      }
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  replaceDemoPortfolio(portfolio) {
    const index = this.demoPortfolios.findIndex(
      (row) => row.portfolio_id === portfolio.portfolio_id,
    );
    if (index >= 0) {
      this.demoPortfolios[index] = normalizeDemoPortfolio(portfolio);
    }
  }

  markDemoRecommendationExecuted(trade) {
    const action = String(trade.action || "").toUpperCase();
    const ticker = String(trade.ticker || "").toUpperCase();
    if (!action || !ticker) {
      return;
    }
    this.demoExecutedActions.add(`${action}:${ticker}`);
  }
}

function rollupDemoPerformancePoints(points, period) {
  const rowsByPeriod = new Map();
  for (const point of points) {
    const key = demoPeriodKey(point.date, period);
    if (!key) {
      continue;
    }
    rowsByPeriod.set(key, {
      ...point,
      period: key,
    });
  }
  return [...rowsByPeriod.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, point]) => point);
}

function demoPeriodKey(dateText, period) {
  const date = new Date(`${dateText}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  if (period === "daily") {
    return dateText;
  }
  if (period === "weekly") {
    return demoIsoWeekKey(date);
  }
  if (period === "yearly") {
    return dateText.slice(0, 4);
  }
  return dateText.slice(0, 7);
}

function demoIsoWeekKey(date) {
  const target = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

let demoLotSequence = 0;

const DEMO_PORTFOLIOS = [
  normalizeDemoPortfolio({
    cash: 0,
    created_at: "2026-06-11T00:00:00Z",
    holdings: [
      {
        lots: [
          {
            cost_basis_per_share: 120,
            purchase_date: "2020-01-01",
            shares: 10,
          },
        ],
        shares: 10,
        ticker: "MSFT",
      },
      {
        lots: [
          {
            cost_basis_per_share: 90,
            purchase_date: "2020-01-01",
            shares: 5,
          },
          {
            cost_basis_per_share: 90,
            purchase_date: "2021-06-01",
            shares: 3,
          },
        ],
        shares: 8,
        ticker: "GOOGL",
      },
    ],
    name: "Demo Taxable Account",
    activation_date: "2026-04-01",
    performance_start_date: "2026-04-01",
    notifications: {
      emails: ["local-demo@jestle.com"],
      enabled: false,
    },
    owner_email: "local-demo@jestle.com",
    portfolio_id: "demo-taxable",
    status: "active",
    updated_at: "2026-06-11T00:00:00Z",
    viewer_emails: [],
  }),
];

const DEMO_ACTIVITY = [
  {
    activity_id: "demo-trade-1",
    activity_type: "trade_buy",
    details: {
      action: "BUY",
      execution_price: 180,
      fees: 1.8,
      shares: 2,
      ticker: "NVDA",
      trade_value: 360,
    },
    recorded_at: "2026-06-02T12:00:00Z",
    title: "Bought 2 NVDA",
  },
];

function demoRecommendationActions(executedActions = new Set(), portfolio = {}) {
  const tickers = [
    ["SELL", "MSFT", 10, 340, 120],
    ["BUY", "NVDA", 10, 180, 0],
    ["BUY", "AAPL", 8, 210, 0],
    ["BUY", "AMZN", 6, 185, 0],
    ["SELL", "GOOGL", 3, 180, 32],
    ["BUY", "META", 4, 520, 0],
    ["BUY", "AVGO", 5, 170, 0],
    ["BUY", "AMD", 9, 155, 0],
    ["SELL", "TSLA", 2, 220, 15],
    ["BUY", "COST", 1, 910, 0],
    ["BUY", "NFLX", 2, 660, 0],
    ["BUY", "CRM", 4, 280, 0],
    ["SELL", "INTC", 11, 31, 4],
    ["BUY", "ADBE", 2, 510, 0],
    ["BUY", "QCOM", 7, 205, 0],
    ["BUY", "ORCL", 6, 145, 0],
    ["SELL", "PYPL", 5, 67, 3],
    ["BUY", "NOW", 1, 740, 0],
    ["BUY", "SHOP", 5, 96, 0],
    ["BUY", "ARM", 3, 120, 0],
  ];
  return tickers.map(([action, ticker, shares, price, tax], index) => {
    const tradeValue = shares * price;
    const executed = executedActions.has(`${action}:${ticker}`);
    return {
      action,
      action_id: `demo-action-${index + 1}`,
      executed_shares: executed ? shares : 0,
      executed_trade_count: executed ? 1 : 0,
      estimated_price: price,
      estimated_tax: tax,
      estimated_trade_value: tradeValue,
      estimated_transaction_cost: Math.round(tradeValue * 0.001 * 100) / 100,
      lots_to_sell:
        action === "SELL"
          ? demoLotsToSell(portfolio, ticker, shares)
          : [],
      remaining_shares: executed ? 0 : shares,
      reason:
        action === "SELL"
          ? "above target or outside latest volume target set"
          : "below latest volume target weight after tax-budgeted sells",
      shares,
      status: executed ? "executed" : "pending",
      ticker,
    };
  });
}

function demoLotsToSell(portfolio, ticker, requestedShares) {
  const holding = (portfolio.holdings || []).find((row) => row.ticker === ticker);
  let remaining = Number(requestedShares || 0);
  const selected = [];
  for (const lot of holding?.lots || []) {
    const shares = Math.min(Number(lot.shares || 0), remaining);
    if (shares > 0) {
      selected.push({ lot_id: lot.lot_id, shares });
      remaining -= shares;
    }
    if (remaining <= 1e-9) {
      break;
    }
  }
  return selected;
}

function newDemoLotId(ticker) {
  demoLotSequence += 1;
  return `demo-${String(ticker || "holding").toLowerCase()}-${demoLotSequence}`;
}

function normalizeDemoPortfolio(portfolio) {
  return {
    base_currency: "USD",
    activation_date: portfolio.activation_date || "",
    cash: Number(portfolio.cash || 0),
    created_at: portfolio.created_at || new Date().toISOString(),
    holdings: (portfolio.holdings || []).map((holding) => ({
      ...holding,
      lots: (holding.lots || []).map((lot) => ({
        ...lot,
        lot_id: lot.lot_id || newDemoLotId(holding.ticker),
      })),
    })),
    manual_ticker_states: portfolio.manual_ticker_states || [],
    name: portfolio.name || "Portfolio",
    performance_start_date:
      portfolio.performance_start_date || String(portfolio.created_at || "").slice(0, 10),
    notifications: portfolio.notifications || { emails: [], enabled: false },
    owner_email: portfolio.owner_email || "local-demo@jestle.com",
    permissions: { can_read: true, can_write: true },
    portfolio_id: portfolio.portfolio_id,
    rebalance: portfolio.rebalance || {
      annual_tax_budget_fraction: 0.04,
      max_new_position_weight: 0.05,
      max_trades_per_rebalance: 30,
      min_trade_dollars: 1000,
      sell_threshold: 0.8,
      strategy: "volume",
    },
    schema_version: 1,
    status: portfolio.status || "draft",
    tax_profile: portfolio.tax_profile || {
      federal_long_term_rate: 0.2,
      federal_short_term_rate: 0.37,
      niit_rate: 0.038,
      state_rate: 0.044,
    },
    time_zone: portfolio.time_zone || "America/Denver",
    updated_at: portfolio.updated_at || new Date().toISOString(),
    viewer_emails: portfolio.viewer_emails || [],
  };
}

function demoActivityFromTrade(trade) {
  const action = String(trade.action || "").toUpperCase();
  const verb = action === "BUY" ? "Bought" : "Sold";
  return {
    activity_id: `demo-trade-${Date.now()}`,
    activity_type: action === "BUY" ? "trade_buy" : "trade_sell",
    details: trade,
    effective_date: trade.trade_date,
    recorded_at: new Date().toISOString(),
    title: `${verb} ${trade.shares} ${trade.ticker}`,
  };
}

function demoActivityFromCashFlow(cashFlow) {
  const contribution = Number(cashFlow.cash_delta || 0) >= 0;
  return {
    activity_id: `demo-cash-flow-${Date.now()}`,
    activity_type: contribution ? "cash_contribution" : "cash_withdrawal",
    details: {
      amount: cashFlow.amount,
      cash_delta: cashFlow.cash_delta,
      effective_date: cashFlow.effective_date,
    },
    effective_date: cashFlow.effective_date,
    recorded_at: new Date().toISOString(),
    title: `${contribution ? "Cash contribution" : "Cash withdrawal"} $${Number(
      cashFlow.amount || 0,
    ).toFixed(2)}`,
  };
}

function demoActivityFromDividend(dividend) {
  const ticker = String(dividend.ticker || "").trim().toUpperCase();
  return {
    activity_id: `demo-dividend-${Date.now()}`,
    activity_type: "dividend_income",
    details: {
      amount: dividend.amount,
      cash_delta: dividend.amount,
      effective_date: dividend.effective_date,
      estimated_tax: dividend.estimated_tax,
      ticker,
    },
    effective_date: dividend.effective_date,
    recorded_at: new Date().toISOString(),
    title: `${ticker ? `${ticker} dividend` : "Dividend income"} $${Number(
      dividend.amount || 0,
    ).toFixed(2)}`,
  };
}

function demoActivityFromTaxPayment(payment) {
  return {
    activity_id: `demo-tax-payment-${Date.now()}`,
    activity_type: "tax_payment",
    details: {
      ...payment,
      cash_delta: -Number(payment.amount || 0),
    },
    effective_date: payment.effective_date,
    recorded_at: new Date().toISOString(),
    title: `${payment.jurisdiction === "federal" ? "Federal" : "Colorado"} tax payment $${Number(
      payment.amount || 0,
    ).toFixed(2)}`,
  };
}

function recommendationSummary(recommendationId, recommendation) {
  return {
    action_count: (recommendation.actions || []).length,
    as_of_date: recommendation.as_of_date,
    executed_action_count: recommendation.workflow?.executed_action_count || 0,
    estimated_tax: recommendation.summary?.estimated_tax || 0,
    estimated_transaction_cost:
      recommendation.summary?.estimated_transaction_cost || 0,
    generated_at: recommendation.generated_at,
    pending_action_count: recommendation.workflow?.pending_action_count || 0,
    portfolio_value: recommendation.summary?.portfolio_value || 0,
    recommendation_id: recommendationId,
    target: recommendation.target || {},
    trade_count: recommendation.summary?.trade_count || 0,
    workflow_status: recommendation.workflow?.status || "",
  };
}

function applyDemoTrade(portfolio, trade) {
  const action = String(trade.action || "").toUpperCase();
  const ticker = String(trade.ticker || "").toUpperCase();
  const shares = Number(trade.shares || 0);
  const executionPrice = Number(trade.execution_price || trade.price || 0);
  const fees = Number(trade.fees || 0);
  const fundingSource = String(trade.funding_source || "portfolio_cash");
  const tradeDate = trade.trade_date || new Date().toISOString().slice(0, 10);
  if (
    !ticker
    || !Number.isFinite(shares)
    || shares <= 0
    || !Number.isFinite(executionPrice)
    || executionPrice <= 0
    || !Number.isFinite(fees)
    || fees < 0
  ) {
    throw new Error("Invalid trade");
  }
  if (action === "BUY") {
    return applyDemoBuy(portfolio, {
      executionPrice,
      fees,
      fundingSource,
      shares,
      ticker,
      tradeDate,
    });
  }
  if (action === "SELL") {
    return applyDemoSell(portfolio, {
      executionPrice,
      fees,
      shares,
      soldLots: trade.sold_lots || [],
      ticker,
      tradeDate,
    });
  }
  throw new Error("Invalid trade action");
}

function applyDemoBuy(portfolio, trade) {
  const tradeValue = trade.shares * trade.executionPrice;
  const totalCashNeeded = tradeValue + trade.fees;
  const externalContribution =
    trade.fundingSource === "external_contribution" ? totalCashNeeded : 0;
  const nextCash =
    Number(portfolio.cash || 0) + externalContribution - totalCashNeeded;
  if (nextCash < 0) {
    throw new Error("Insufficient portfolio cash for buy");
  }
  const holdings = [...(portfolio.holdings || [])];
  const index = holdings.findIndex((holding) => holding.ticker === trade.ticker);
  const lot = {
    cost_basis_per_share: (tradeValue + trade.fees) / trade.shares,
    purchase_date: trade.tradeDate,
    shares: trade.shares,
  };
  if (index >= 0) {
    const lots = [...(holdings[index].lots || []), lot];
    holdings[index] = {
      ...holdings[index],
      lots,
      shares: lots.reduce((total, row) => total + Number(row.shares || 0), 0),
    };
  } else {
    holdings.push({ lots: [lot], shares: trade.shares, ticker: trade.ticker });
  }
  const updated = normalizeDemoPortfolio({
    ...portfolio,
    cash: nextCash,
    holdings,
    updated_at: new Date().toISOString(),
  });
  return {
    portfolio: updated,
    schema_version: 1,
    trade: {
      action: "BUY",
      cash_delta: -tradeValue - trade.fees,
      execution_price: trade.executionPrice,
      external_contribution: externalContribution,
      fees: trade.fees,
      funding_source: trade.fundingSource,
      shares: trade.shares,
      ticker: trade.ticker,
      trade_date: trade.tradeDate,
      trade_value: tradeValue,
    },
  };
}

function applyDemoSell(portfolio, trade) {
  const holdings = [...(portfolio.holdings || [])];
  const index = holdings.findIndex((holding) => holding.ticker === trade.ticker);
  if (index < 0 || Number(holdings[index].shares || 0) < trade.shares) {
    throw new Error("Not enough shares to sell");
  }
  const soldByLotId = new Map(
    trade.soldLots.map((lot) => [
      String(lot.lot_id || ""),
      Number(lot.shares || 0),
    ]),
  );
  const soldShares = [...soldByLotId.values()].reduce(
    (total, shares) => total + shares,
    0,
  );
  if (
    !soldByLotId.size
    || soldByLotId.size !== trade.soldLots.length
    || [...soldByLotId.keys()].some((lotId) => !lotId)
    || [...soldByLotId.values()].some(
      (shares) => !Number.isFinite(shares) || shares <= 0,
    )
    || Math.abs(soldShares - trade.shares) > 1e-6
  ) {
    throw new Error("Exact sold tax lots are required");
  }
  const lots = [];
  for (const lot of holdings[index].lots || []) {
    const sold = soldByLotId.get(lot.lot_id) || 0;
    if (sold > Number(lot.shares || 0)) {
      throw new Error("Sold shares exceed the selected tax lot");
    }
    soldByLotId.delete(lot.lot_id);
    const retained = Number(lot.shares || 0) - sold;
    if (retained > 0) {
      lots.push({ ...lot, shares: retained });
    }
  }
  if (soldByLotId.size) {
    throw new Error("Selected tax lot was not found");
  }
  const tradeValue = trade.shares * trade.executionPrice;
  const nextCash = Number(portfolio.cash || 0) + tradeValue - trade.fees;
  if (lots.length) {
    holdings[index] = {
      ...holdings[index],
      lots,
      shares: lots.reduce((total, row) => total + Number(row.shares || 0), 0),
    };
  } else {
    holdings.splice(index, 1);
  }
  const updated = normalizeDemoPortfolio({
    ...portfolio,
    cash: nextCash,
    holdings,
    updated_at: new Date().toISOString(),
  });
  return {
    portfolio: updated,
    schema_version: 1,
    trade: {
      action: "SELL",
      cash_delta: tradeValue - trade.fees,
      execution_price: trade.executionPrice,
      fees: trade.fees,
      realized_gain: 0,
      shares: trade.shares,
      sold_lots: trade.soldLots,
      ticker: trade.ticker,
      trade_date: trade.tradeDate,
      trade_value: tradeValue,
    },
  };
}
